import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Wallet } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { fmt, tooltipStyle } from "./oversiktConstants";
import { dec } from "@/lib/formatters";

// Household debt, wealth and income after tax side by side (estimates per household)
const HusholdningCompareChart = ({ boligData }) => {
  const rows = Object.entries(boligData || {})
    .filter(([, k]) => k.husholdning)
    .map(([, k]) => ({
      name: k.name,
      "Gjeld per hushald": k.husholdning.gjeld_husholdning ?? null,
      "Bruttoformue per hushald": k.husholdning.formue_husholdning ?? null,
      "Inntekt etter skatt (median)": k.husholdning.etter_skatt ?? null,
      gjeldsgrad: k.husholdning.gjeldsgrad,
      year: k.husholdning.year,
    }));
  if (!rows.length) return null;

  return (
    <Card className="bg-white border border-[#E6E9F0] rounded-lg lg:col-span-2" data-testid="compare-husholdning-chart">
      <CardHeader className="border-b border-[#E6E9F0] pb-4">
        <CardTitle className="text-lg font-medium text-[#1F2A36] flex items-center gap-2">
          <Wallet className="w-5 h-5 text-[#103766]" />
          Gjeld, formue og inntekt i hushalda {rows[0].year} (kr)
          <Badge variant="outline" className="ml-2 border-[#E8A34A] text-[#B8741F] bg-[#FDF6EA] text-[10px] uppercase">Estimat</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-6">
        <div className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={rows} barCategoryGap="25%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E6E9F0" />
              <XAxis dataKey="name" stroke="#8AA4BF" fontSize={12} />
              <YAxis stroke="#8AA4BF" fontSize={12} tickFormatter={(v) => `${dec((v / 1000000), 1)}M`} />
              <Tooltip formatter={(v) => `${fmt(v)} kr`} contentStyle={tooltipStyle} />
              <Legend itemSorter={null} />
              <Bar dataKey="Gjeld per hushald" fill="#D35E4E" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Bruttoformue per hushald" fill="#103766" radius={[4, 4, 0, 0]} />
              <Bar dataKey="Inntekt etter skatt (median)" fill="#3F8A86" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="flex flex-wrap gap-4 mt-4" data-testid="compare-gjeldsgrad">
          {rows.map((r) => (
            <div key={r.name} className="text-sm text-[#4B647F]">
              <span className="font-medium text-[#1F2A36]">{r.name}</span>: gjeldsgrad {r.gjeldsgrad != null ? dec(r.gjeldsgrad, 2) : "—"}
            </div>
          ))}
        </div>
        <p className="text-xs text-[#8AA4BF] mt-3">
          Gjeld og bruttoformue per hushald er anslag: gjennomsnitt per person 17 år+ (SSB 05854) × berekna tal på personar 17+ per hushald.
          Inntekt etter skatt er median for alle hushald (SSB 06944). Gjeldsgrad = gjennomsnittleg gjeld / bruttoinntekt per person.
        </p>
      </CardContent>
    </Card>
  );
};

export default HusholdningCompareChart;
